import { useEffect, useMemo, useRef, useState } from 'react';
import { store } from '../store';
import { useAppSelector } from './redux';
import { releaseDecodedFloorRasterTile } from '../utils/floorRasterCache';
import { floorRasterService } from '../utils/floorRasterService';
import { shouldUseSimulationDxfRaster } from '../utils/floorRasterMode';
import {
  floorRasterCappedTileSpecs,
  floorRasterLevelForDensity,
  floorRasterTileSpecs,
  FLOOR_RASTER_QUALITY_SCALE,
  type FloorRasterDataset,
  type FloorRasterDatasetRegistration,
  type FloorRasterDecodedTile,
  type FloorRasterViewQuery,
} from '../utils/floorRasterTypes';

export type SimulationDxfRasterResult = {
  enabled: boolean;
  ready: boolean;
  loading: boolean;
  registration: FloorRasterDatasetRegistration | null;
  overviewTiles: FloorRasterDecodedTile[];
  detailTiles: FloorRasterDecodedTile[];
  requestedLevel: number;
  error: string | null;
};

function releaseTiles(tiles: FloorRasterDecodedTile[]) {
  for (const tile of tiles) releaseDecodedFloorRasterTile(tile.key);
}

function releaseTilesAfterPresentation(tiles: FloorRasterDecodedTile[]) {
  if (tiles.length === 0) return;
  if (typeof requestAnimationFrame !== 'function') {
    setTimeout(() => releaseTiles(tiles), 0);
    return;
  }
  // React commits the replacement first; the second frame guarantees R3F had
  // an opportunity to upload/present it before memory pressure may close the
  // superseded ImageBitmap.
  requestAnimationFrame(() => requestAnimationFrame(() => releaseTiles(tiles)));
}

/**
 * Shared 2D/3D DXF raster source.
 *
 * The hook intentionally subscribes only to floor appearance and view mode.
 * It never reads simulation time, vehicles, inventory, or stream events,
 * so those high-frequency updates cannot rebuild the static background.
 */
export function useSimulationDxfRaster(
  query?: FloorRasterViewQuery | null,
  forceEnabled = false,
  detailMaxTiles = 48,
  suspendOverview = false
): SimulationDxfRasterResult {
  const simulationPanelOpen = useAppSelector((state) => state.ui.simulationPanelOpen);
  const view3dOpen = useAppSelector((state) => state.ui.view3dOpen);
  const view3dSimulation = useAppSelector((state) => state.ui.view3dSimulation);
  const simulationRunning = useAppSelector((state) => state.simulation.running);
  const polylines = useAppSelector((state) => state.floorplan.polylines);
  const polylineLayers = useAppSelector((state) => state.floorplan.polylineLayers);
  const polylineMeta = useAppSelector((state) => state.floorplan.polylineMeta);
  const layers = useAppSelector((state) => state.floorplan.layers);
  const labels = useAppSelector((state) => state.floorplan.labels);
  const bounds = useAppSelector((state) => state.floorplan.bounds);
  const floorPlanId = useAppSelector((state) => state.floorplan.floorPlanId);
  const name = useAppSelector((state) => state.floorplan.name);
  const hiddenEntityTypes = useAppSelector((state) => state.floorplan.hiddenEntityTypes);
  const opacity = useAppSelector((state) => state.floorplan.opacity);
  const showLabels = useAppSelector((state) => state.floorplan.showLabels);
  const visible = useAppSelector((state) => state.floorplan.visible);

  const enabled = shouldUseSimulationDxfRaster({
    hasDxfFloor: Boolean(bounds) && polylines.length > 0 && visible,
    simulationPanelOpen,
    simulationRunning,
    view3dSimulation,
    view3dOpen,
  }) || (forceEnabled && Boolean(bounds) && polylines.length > 0 && visible);

  const dataset = useMemo<FloorRasterDataset | null>(() => {
    if (!bounds || polylines.length === 0) return null;
    return {
      sourceId: floorPlanId || name || 'local-floor-plan',
      polylines,
      polylineLayers,
      polylineMeta,
      layers,
      labels,
      bounds,
      hiddenEntityTypes,
      opacity,
      showLabels,
    };
  }, [
    bounds,
    floorPlanId,
    hiddenEntityTypes,
    labels,
    layers,
    name,
    opacity,
    polylineLayers,
    polylineMeta,
    polylines,
    showLabels,
  ]);

  const [registration, setRegistration] = useState<FloorRasterDatasetRegistration | null>(null);
  const [registrationKey, setRegistrationKey] = useState('');
  const [overviewTiles, setOverviewTiles] = useState<FloorRasterDecodedTile[]>([]);
  const [detailTiles, setDetailTiles] = useState<FloorRasterDecodedTile[]>([]);
  const heldOverviewTiles = useRef<FloorRasterDecodedTile[]>([]);
  const heldDetailTiles = useRef<FloorRasterDecodedTile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const datasetKey = dataset ? floorRasterService.runtimeDatasetKey(dataset) : '';
  const currentRegistration = registrationKey === datasetKey ? registration : null;

  useEffect(() => {
    if (dataset) floorRasterService.scheduleOverviewPrewarm(dataset);
    else floorRasterService.reset();
  }, [dataset, datasetKey]);

  useEffect(() => {
    let cancelled = false;
    if (!enabled || !dataset) {
      queueMicrotask(() => {
        if (cancelled) return;
        setRegistration(null);
        setRegistrationKey('');
        setLoading(false);
        setError(null);
      });
      return () => {
        cancelled = true;
      };
    }
    queueMicrotask(() => {
      if (cancelled) return;
      setLoading(true);
      setError(null);
    });
    void floorRasterService
      .prepare(dataset)
      .then((next) => {
        if (!cancelled) {
          setRegistration(next);
          setRegistrationKey(datasetKey);
        }
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        setError(reason instanceof Error ? reason.message : String(reason));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataset, datasetKey, enabled]);

  useEffect(() => {
    let cancelled = false;
    if (!enabled || !currentRegistration || suspendOverview) {
      if (!enabled || !dataset || suspendOverview) {
        queueMicrotask(() => {
          if (cancelled) return;
          releaseTiles(heldOverviewTiles.current);
          heldOverviewTiles.current = [];
          setOverviewTiles([]);
        });
      }
      return () => {
        cancelled = true;
      };
    }
    const specs = floorRasterTileSpecs(currentRegistration, 0);
    void floorRasterService
      .loadDecoded(currentRegistration, specs)
      .then((tiles) => {
        if (cancelled) {
          releaseTiles(tiles);
          return;
        }
        const previous = heldOverviewTiles.current;
        heldOverviewTiles.current = tiles;
        setOverviewTiles(tiles);
        releaseTilesAfterPresentation(previous);
        setLoading(false);
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        setError(reason instanceof Error ? reason.message : String(reason));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentRegistration, enabled, dataset, suspendOverview]);

  const requestedLevel =
    currentRegistration && query
      ? floorRasterLevelForDensity(
          currentRegistration.bounds,
          query.pixelsPerWorldUnit * FLOOR_RASTER_QUALITY_SCALE
        )
      : 0;
  const detailSpecs =
    currentRegistration && query && requestedLevel > 0
      ? floorRasterCappedTileSpecs(
          currentRegistration,
          requestedLevel,
          query.worldBounds,
          detailMaxTiles
        )
      : [];
  // Camera coordinates change continuously, but tile contents only change
  // when the visible tile address set changes. Depending on addresses prevents
  // decode/release/re-render churn while orbiting or panning inside a tile.
  const detailSpecsKey = detailSpecs.map((spec) => spec.key).join(',');

  useEffect(() => {
    let cancelled = false;
    if (
      !enabled ||
      !currentRegistration ||
      !query ||
      requestedLevel === 0 ||
      detailMaxTiles <= 0
    ) {
      if (!enabled || !dataset || requestedLevel === 0 || detailMaxTiles <= 0) {
        queueMicrotask(() => {
          if (cancelled) return;
          releaseTiles(heldDetailTiles.current);
          heldDetailTiles.current = [];
          setDetailTiles([]);
        });
      }
      return () => {
        cancelled = true;
      };
    }
    void floorRasterService
      .loadDecoded(currentRegistration, detailSpecs)
      .then((tiles) => {
        if (cancelled) {
          releaseTiles(tiles);
          return;
        }
        const previous = heldDetailTiles.current;
        heldDetailTiles.current = tiles;
        setDetailTiles(tiles);
        releaseTilesAfterPresentation(previous);
      })
      .catch(() => {
        // The complete overview remains visible; detail failure is non-blocking.
      });
    return () => {
      cancelled = true;
    };
    // The key represents the exact tile address set; raw camera bounds are
    // deliberately excluded because they change every animation frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRegistration, detailMaxTiles, detailSpecsKey, enabled, requestedLevel, dataset]);

  useEffect(
    () => () => {
      const overview = heldOverviewTiles.current;
      const detail = heldDetailTiles.current;
      heldOverviewTiles.current = [];
      heldDetailTiles.current = [];
      // Konva unmounts when 3D is revealed; defer release so the WebGL path can
      // acquire the same overview tiles before ImageBitmaps are evicted.
      if (store.getState().ui.view3dOpen) {
        releaseTilesAfterPresentation(overview);
        releaseTilesAfterPresentation(detail);
        return;
      }
      releaseTiles(overview);
      releaseTiles(detail);
    },
    []
  );

  return {
    enabled,
    ready: enabled && Boolean(currentRegistration) && overviewTiles.length > 0,
    loading,
    registration: currentRegistration,
    overviewTiles,
    detailTiles,
    requestedLevel,
    error,
  };
}

